import { tokenStorage } from '@/utils/token-storage';
import { ROLES } from '@/constants/roles';

/**
 * Normalize one stored role entry to its canonical identifiers.
 *
 * The stored user object has two shapes depending on how it was last written:
 *   - login payload      → `roles: ['role-owner', …]`             (flat slugs)
 *   - usePermissionsSync → `roles: [{ role: { id, slug, name } }]` (nested)
 *
 * `role.id` is a UUID, so the nested shape must be matched on `slug` (or the
 * display name). Reading `id` here is what silently broke every role check
 * five minutes after login, when the first background sync replaced the flat
 * login payload with the nested one.
 */
function roleIdentifiers(entry: any): string[] {
    if (typeof entry === 'string') return [entry];
    if (entry && typeof entry === 'object') {
        const role = entry.role ?? entry;
        return [role?.slug, role?.name].filter(Boolean);
    }
    return [];
}

/** Every identifier (slug and display name) across a user's roles — for matching. */
function allRoleIdentifiers(user: any): string[] {
    if (!Array.isArray(user?.roles)) return [];
    return user.roles.flatMap(roleIdentifiers);
}

/** One canonical identifier per role — slug preferred, display name as fallback. */
function canonicalRoleIds(user: any): string[] {
    if (!Array.isArray(user?.roles)) return [];
    return user.roles.map((entry: any) => roleIdentifiers(entry)[0]).filter(Boolean);
}

/** Slug, official name, and the informal label some older Owner rows still use. */
const OWNER_IDENTIFIERS = new Set<string>([
    ROLES.OWNER.slug,
    ROLES.OWNER.name,
    'Owner',
]);

function decodeAccessTokenPayload(): any | null {
    const token = tokenStorage.getToken();
    if (!token) return null;
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch {
        return null;
    }
}

export function userIsOrganizationOwner(user: any): boolean {
    if (!user) return false;
    if (user.isOwner === true || user.isPlatformAdmin === true) return true;
    return allRoleIdentifiers(user).some((id) => OWNER_IDENTIFIERS.has(id));
}

// Auth utility functions
export const authUtils = {
    // Check if user is authenticated
    isAuthenticated: (): boolean => {
        return tokenStorage.hasToken();
    },

    // Get current user from localStorage (preferred) or token fallback
    getCurrentUser: () => {
        const userData = tokenStorage.getUserData();
        const jwt = decodeAccessTokenPayload();
        if (userData) {
            // usePermissionsSync can overwrite the login snapshot with a
            // payload that omits isOwner. The JWT still carries the flag —
            // restore it so Owner never loses Financial / Procurement view.
            if (userData.isOwner !== true && jwt?.isOwner === true) {
                return { ...userData, isOwner: true };
            }
            return userData;
        }
        return jwt;
    },

    /** Account Owner (flag, JWT, or Owner role) — Financial view is default. */
    isOrganizationOwner: (user?: any): boolean => {
        return userIsOrganizationOwner(user ?? authUtils.getCurrentUser());
    },

    // Store user data after login
    setUserData: (userData: any): void => {
        tokenStorage.setUserData(userData);
    },

    // Logout user
    logout: (): void => {
        tokenStorage.clearAll();
        // Redirect to login page
        window.location.href = '/login';
    },

    // Get organization ID from stored user data
    getOrganizationId: (): string | null => {
        const user = authUtils.getCurrentUser();
        return user?.organizationId || null;
    },

    // Check if user has a specific permission
    hasPermission: (permission: string): boolean => {
        const user = authUtils.getCurrentUser();
        if (!user) return false;

        // The organization owner (and platform admins) always have every
        // permission — unconditionally. This keeps nav, settings, and component
        // gating fully open for the owner regardless of their stored permission
        // list (mirrors the back-end PermissionsGuard owner bypass).
        if (userIsOrganizationOwner(user)) return true;

        if (!user.permissions) return false;

        // Handle both formats: string array and object array
        if (Array.isArray(user.permissions)) {
            return user.permissions.some((perm: any) => {
                // New format: { permission: { id: "...", name: "perm-..." } }
                if (typeof perm === 'object' && perm.permission) {
                    return perm.permission.id === permission || perm.permission.name === permission;
                }
                // Legacy format: simple string array
                return perm === permission;
            });
        }
        return false;
    },

    // Check if user has a specific role (by slug or display name)
    hasRole: (role: string): boolean => {
        const user = authUtils.getCurrentUser();
        if (!user?.roles) return false;

        return allRoleIdentifiers(user).includes(role);
    },

    // Get all user permissions (returns permission names)
    getUserPermissions: (): string[] => {
        const user = authUtils.getCurrentUser();
        if (!user?.permissions || !Array.isArray(user.permissions)) return [];

        return user.permissions.map((perm: any) => {
            // New format: { permission: { id: "perm-...", name: "..." } }
            if (typeof perm === 'object' && perm.permission?.name) {
                return perm.permission.name;
            }
            // Legacy format: simple string
            return perm;
        }).filter(Boolean);
    },

    // Get all user roles (returns role slugs, falling back to display names).
    // Callers compare these against ROLES.*.slug, so a UUID must never leak in.
    getUserRoles: (): string[] => {
        const user = authUtils.getCurrentUser();
        if (!user?.roles || !Array.isArray(user.roles)) return [];

        return canonicalRoleIds(user);
    }
};
